import { match } from "ts-pattern";
import {
  getLocationSpecificContent,
  matchesAny
} from "src/utils/localization/geolocation";
import {
  AirNonAirOrLocationFilterFragment,
  EmotionalSlideContentFragment,
  EmotionalSlidePromoContentFragment,
  HeroSlideContentFragment,
  HeroSlidePromoContentFragment,
  SanityMultilingualString,
  SanityMultilingualText
} from "types/graphql-types";
import { Geolocation } from "../../utils/localization/geolocation/geolocation";
import { PageBuilderButtonCTAProps } from "components/PageBuilder/PageBuilderButtonCTA";
import { isPresent } from "src/utils/checks";
import {
  BlockContentValue,
  PartialSanityImageType
} from "@silversea-ssc/ui-components-react";
import { RemotePromos } from "src/ssc-api/useRemotePromos";
import { byConfigurationCode } from "src/utils/promos";

type HeroBannerSlideshowItemBase = {
  _key: string | undefined;
  locationFilters: AirNonAirOrLocationFilterFragment[] | undefined;
  image: PartialSanityImageType | undefined;
  ctaButtons: PageBuilderButtonCTAProps[] | undefined;
  customKey: string | undefined;
};

export type HeroBannerSlideshowItem = HeroBannerSlideshowItemBase &
  (
    | {
        _type: "slide";
        title: string;
        content: string;
      }
    | {
        _type: "promo";
        promoConfigurationCode: string | undefined;
        promo: RemotePromos[number];
        title: BlockContentValue | undefined;
        content: BlockContentValue | undefined;
        showCountdown: boolean;
        countdownLabel: string | undefined;
      }
  );

export const filterActiveSlidesForGeolocation =
  (geolocation: Geolocation) =>
  (
    slide:
      | HeroSlideContentFragment
      | HeroSlidePromoContentFragment
      | EmotionalSlidePromoContentFragment
      | EmotionalSlideContentFragment
  ) => {
    return matchesAny(slide.locationFilters, geolocation);
  };

export const sanityContentToHeroBannerSlideshowItem =
  (geolocation: Geolocation, activePromos: RemotePromos | undefined) =>
  (
    slide: HeroSlideContentFragment | HeroSlidePromoContentFragment
  ): HeroBannerSlideshowItem | undefined => {
    return match(slide)
      .with({ __typename: "SanityHeroSlideContent" }, (props) => {
        const title = getLocationSpecificContent<SanityMultilingualString>(
          geolocation,
          props?.title
        )?.localized;

        const content = getLocationSpecificContent<SanityMultilingualText>(
          geolocation,
          props?.content
        )?.localized;

        return {
          _key: props._key,
          image: props.image,
          locationFilters: props.locationFilters?.filter(isPresent),
          ctaButtons: props.ctaButtons?.filter(isPresent),
          customKey: props.customKey,
          _type: "slide",
          title: title || "",
          content: content || ""
        } satisfies HeroBannerSlideshowItem;
      })
      .with({ __typename: "SanityHeroSlidePromoContent" }, (props) => {
        const promo = activePromos?.find(
          byConfigurationCode(props.promo?.configuration?.code)
        );
        if (!promo) return undefined;

        return {
          _key: props._key,
          image: props.image,
          locationFilters: props.locationFilters?.filter(isPresent),
          ctaButtons: props.ctaButtons?.filter(isPresent),
          customKey: props.customKey,
          _type: "promo",
          promoConfigurationCode: props.promo?.configuration?.code || "",
          promo: promo,
          title: props.blockTitle?.localized,
          content: props.blockContent?.localized,
          showCountdown: props?.showCountdown ?? false,
          countdownLabel:
            props.countdownLabel?.localized ??
            props.promo?.countdownLabel?.localized
        } satisfies HeroBannerSlideshowItem;
      })
      .exhaustive();
  };
